/**
 * "Request purchase" from a Sites BOQ line (Sites BOQ idea 5): nothing is in stock, so the line goes
 * to Procurement as a site request (POST /site-requests) — quantity and needed-on-site date — where the
 * Procurement page lists it with the other requests and their due dates. When the project already has an
 * open request for the line (`existing` = { qty, due, count }), the dialog says so and asks only for the rest.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, ShoppingCart, TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, toDateInputValue } from '@/lib/format';
import { formatQty, plural, toQty } from './inventoryModel';

const DEFAULT_LEAD_DAYS = 14;

const plusDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

export default function RequestPurchaseDialog({ open, onOpenChange, line, detail, existing = null, onRequested }) {
  const [qty, setQty] = useState('');
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !detail) return;
    // What the project still needs: not delivered, not booked and not already requested.
    const openQty = Math.max(0, toQty(detail.remaining_qty) - toQty(detail.booked_qty) - toQty(existing?.qty));
    setQty(String(openQty || ''));
    setDue(plusDays(Math.max(DEFAULT_LEAD_DAYS, toQty(line?.lead_time_days))));
    setNotes('Not in stock — requested from Inventory › Sites BOQ');
    setError('');
  }, [detail, existing, line, open]);

  const submit = async (event) => {
    event.preventDefault();
    const number = Number(qty);
    if (!Number.isFinite(number) || number <= 0) { setError('Enter a quantity above 0'); return; }
    if (!due) { setError('Pick the date it is needed on site'); return; }
    setBusy(true);
    setError('');
    try {
      await api.post('/site-requests', {
        project_id: detail.project_id,
        boq_item_id: detail.boq_item_id,
        requested_qty: number,
        due_date: due,
        notes: notes.trim()
      });
      notify.success(`Purchase requested: ${formatQty(number)} × ${line.part_number} for ${detail.project_name}`, {
        description: 'It is on Procurement › Site requests.'
      });
      onRequested?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not send the request'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg text-ink"><ShoppingCart className="h-5 w-5 text-ink-3" aria-hidden="true" /> Request purchase</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            <span className="font-mono text-ink">{line?.part_number}</span> for <b className="font-semibold text-ink">{detail?.project_name}</b> — nothing in stock, so Procurement gets it as a site request.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="request-purchase-form">
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">{line?.description || 'No description'} · remaining {formatQty(detail?.remaining_qty)} {line?.unit || 'pcs'}</p>
          {existing ? (
            <p className="flex items-start gap-2 rounded-lg bg-status-amber-bg px-3 py-2 text-[12.5px] text-status-amber-ink" data-testid="request-purchase-existing">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                <b className="font-bold">Already requested: {formatQty(existing.qty)} {line?.unit || 'pcs'}</b>
                {existing.due ? ` · due ${formatDate(existing.due, { year: 'auto' })}` : ''} — this sends {existing.count > 1 ? 'another' : 'a second'} request for the rest.
              </span>
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-3">Quantity</span>
              <Input type="number" min="0" step="any" value={qty} onChange={(event) => setQty(event.target.value)} data-testid="request-purchase-qty" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-ink-3">Needed on site by</span>
              <Input type="date" value={due} onChange={(event) => setDue(event.target.value)} data-testid="request-purchase-date" />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-ink-3">Note for Procurement</span>
            <Input value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={500} />
          </label>
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="request-purchase-submit">{busy ? 'Sending…' : 'Send to Procurement'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Request purchase for all N" (Sites BOQ idea 5): nothing is free in the stores and several projects still
 * need the part — one dialog, one needed-by date, one call. The server works out each project's quantity (BOQ −
 * delivered − booked − already requested, the Send-shortage rule, POST /inventory/shortage-requests narrowed to
 * this part) and previews it; Send creates the requests in one go with one undo entry. `entries` = the projects
 * the page offers ({ detail, qty }) — shown until the preview arrives.
 */
export function RequestForProjectsDialog({ open, onOpenChange, line, entries = [], onRequested }) {
  const [due, setDue] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const requestRef = useRef(0);
  const projectIds = entries.map((entry) => entry.detail.project_id);
  const idsKey = projectIds.join('|');
  const partNumber = line?.part_number || '';
  const unit = line?.unit || 'pcs';

  useEffect(() => {
    if (!open) return;
    setDue(plusDays(Math.max(DEFAULT_LEAD_DAYS, toQty(line?.lead_time_days))));
    setPreview(null);
    setError('');
    setNotice('');
  }, [line, open]);

  const loadPreview = async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!projectIds.length || !partNumber || !due) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/inventory/shortage-requests', { project_ids: projectIds, part_numbers: [partNumber], needed_by: due, dry_run: true });
      if (requestId === requestRef.current) setPreview(res.data || null);
    } catch (err) {
      if (requestId === requestRef.current) { setPreview(null); setError(getErrorMessage(err, 'Could not prepare the requests')); }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(loadPreview, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey, partNumber, due]);

  const lines = preview ? (preview.shortage || []).filter((entry) => entry.to_request > 0) : null;
  const shown = lines || entries.map((entry) => ({ project_id: entry.detail.project_id, project_name: entry.detail.project_name, to_request: entry.qty }));
  const total = shown.reduce((sum, entry) => sum + toQty(entry.to_request), 0);

  const send = async (event) => {
    event.preventDefault();
    if (!due) { setError('Pick the date it is needed on site'); return; }
    if (!preview?.plan_key || !preview.request_lines) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api.post('/inventory/shortage-requests', {
        project_ids: projectIds,
        part_numbers: [partNumber],
        needed_by: due,
        dry_run: false,
        plan_key: preview.plan_key
      });
      notify.success(`Purchase requested: ${partNumber} for ${plural(res.data?.created_requests ?? preview.request_lines, 'project')}`, {
        description: `${formatQty(total)} ${unit} · needed on site by ${formatDate(due)} · on Procurement › Site requests`
      });
      onRequested?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        setNotice('Stock or requests changed a moment ago — the quantities above are up to date now. Check them and send again.');
        loadPreview();
      } else {
        setError(getErrorMessage(err, 'Could not send the requests'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="request-all-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg text-ink"><ShoppingCart className="h-5 w-5 text-ink-3" aria-hidden="true" /> Request purchase for {plural(entries.length, 'project')}</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">
            <span className="font-mono text-ink">{partNumber}</span> — nothing is free in the stores, so each project gets its own site request on Procurement › Site requests.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3" data-testid="request-all-form">
          <div className="rounded-xl border border-line" aria-live="polite">
            <ul className="max-h-[240px] divide-y divide-line-2 overflow-y-auto">
              {shown.map((entry) => (
                <li key={entry.project_id} className="flex items-center gap-2 px-3 py-2 text-[13px]" data-testid={`request-all-line-${entry.project_id}`}>
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink">{entry.project_name}</span>
                  <b className="shrink-0 tabular-nums text-ink">{formatQty(entry.to_request)} {unit}</b>
                </li>
              ))}
              {lines && !lines.length ? <li className="px-3 py-3 text-[12.5px] text-ink-3">Nothing to request — every project already has an open request for it.</li> : null}
            </ul>
            <p className="flex items-center gap-2 border-t border-line-2 px-3 py-2 text-[12px] text-ink-3">
              {loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Checking stock and open requests…</> : <>Each = still to deliver − booked − already requested · {formatQty(total)} {unit} in all</>}
            </p>
          </div>
          <label className="block">
            <span className="mb-1 block text-[12px] font-semibold text-ink-3">Needed on site by</span>
            <Input type="date" value={due} onChange={(event) => setDue(event.target.value)} data-testid="request-all-date" />
          </label>
          {notice ? <p className="text-[12.5px] font-semibold text-status-amber-ink" role="status">{notice}</p> : null}
          {error ? <p className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || loading || !preview?.request_lines} data-testid="request-all-submit">
              {busy ? 'Sending…' : preview?.request_lines ? `Send ${plural(preview.request_lines, 'request')}` : 'Send'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
